import React from "react";
import { useTranslation } from "react-i18next";
import { Loader2, ScanFace } from "lucide-react";
import { bucketById, planTotal } from "@/shared/face-qc/index.mjs";
import type { ScanPhotoLabel } from "@/lib/facelock";
import { captionFor, rememberCaption, type CaptionSubject } from "@/lib/face-scan/caption";
import { coverageOf, emptySet, extraCount, framesToFiles, orderedFrames, planOf, trimToBase, type ScanFrame, type ScanSet } from "@/lib/face-scan/session";
import { clearSavedScan, loadScan, saveScan, takeSavingStoppedNotice } from "@/lib/face-scan/store";
import { Button } from "@/components/ui/button";
import type { FaceLockScanSlotProps } from "../NewFaceLockDialog";
import type { FlowResult } from "./ScanFlow";

// FaceLock Scan in the create dialog (its `scanSlot`): one big "Start FaceLock
// Scan" button that opens the full-screen scan, keeps the set while the dialog is open (and in this
// browser's storage, to resume later), and hands the finished set to the
// dialog with onCaptured: 24 frames, each with its training caption
// (lib/face-scan/caption.ts, sent as `captions` with the create request) and
// its label (sent only when the server takes labelled pictures).
// The heavy part (camera, models) loads only when the scan opens.
//
// THE TRIM. A set holding more pictures past its base views than the server
// takes (`extraMax`, eligibility.photo_extra_max: 0 while that is off) is cut
// back to its base views when it is loaded and again when it is handed over,
// so a saved scan with extras still resumes and creates as a plain 24.

const ScanFlow = React.lazy(() => import("./ScanFlow"));

/** A telemetry session id (the shape lib/face-scan/telemetry.ts makes its own), made here for each time the scan opens. */
const newTelemetrySession = () => Math.random().toString(36).slice(2, 12);

/** What a kept picture is, for the create request: its bucket, the part it was taken in, its size and bytes. */
const labelOf = (f: ScanFrame): ScanPhotoLabel => ({
  bucket: f.bucket, kind: bucketById(f.bucket)?.clip ?? "face", w: f.width, h: f.height, bytes: f.blob.size,
});

/** The set as the server takes it: past `extraMax` extras, its base views only. */
const withinExtras = (s: ScanSet, extraMax: number): ScanSet => (extraCount(s) > extraMax ? trimToBase(s) : s);

export type FaceLockScanProps = FaceLockScanSlotProps & {
  /** Who the avatar shows, when known (captions say "his" / "her"; otherwise "the person's"). */
  subject?: CaptionSubject;
};

export default function FaceLockScan({ disabled, onCaptured, extraMax = 0, subject = "person" }: FaceLockScanProps) {
  const { t } = useTranslation();
  const [set, setSet] = React.useState<ScanSet>(() => emptySet());
  const [open, setOpen] = React.useState(false);
  // Each time the scan opens: its telemetry session, so `store_fail` lands in the scan it belongs to.
  const [telemetrySession, setTelemetrySession] = React.useState<string | null>(null);
  const telemetrySessionRef = React.useRef<string | null>(null);
  telemetrySessionRef.current = telemetrySession;
  const openScan = () => {
    if (open) return;
    setTelemetrySession(newTelemetrySession());
    setOpen(true);
  };
  const [handed, setHanded] = React.useState(0);
  const loaded = React.useRef(false);
  const onCapturedRef = React.useRef(onCaptured);
  onCapturedRef.current = onCaptured;

  const extraMaxRef = React.useRef(extraMax);
  extraMaxRef.current = extraMax;

  // A scan left half-way (or finished and not yet used) comes back (THE TRIM: within what the server takes).
  React.useEffect(() => {
    let alive = true;
    void loadScan().then((saved) => {
      if (!alive) return;
      if (saved) setSet((cur) => (cur.frames.length ? cur : withinExtras(saved, extraMaxRef.current)));
      loaded.current = true;
    });
    return () => { alive = false; };
  }, []);

  const handedRef = React.useRef(0);
  const update = React.useCallback((next: ScanSet) => {
    setSet(next);
    // A change after the set was handed over takes it back: Create waits for a complete set again.
    if (handedRef.current) {
      handedRef.current = 0;
      setHanded(0);
      onCapturedRef.current([]);
    }
  }, []);

  // Keep it on this device (never sent) so leaving mid-scan loses nothing. When saving stops (two failed saves in a
  // row), the telemetry hears it once while the page lives (store.ts), in the session of the scan last opened;
  // nothing is said to the person. It keeps the open scan's pace (telemetry.ts: one lane per sender), so the server
  // never refuses it, nor the scan's next batch, for coming inside the other's 2 s.
  React.useEffect(() => {
    if (!loaded.current) return;
    const h = setTimeout(() => {
      void saveScan(set).then((ok) => {
        if (ok || !takeSavingStoppedNotice()) return;
        const session = telemetrySessionRef.current;
        // Loaded here only (the scan's code stays out of the dialog's bundle until it is needed).
        void import("@/lib/face-scan/telemetry").then(({ createScanTelemetry }) => {
          const tel = createScanTelemetry(session ? { session } : {});
          tel.track("store_fail", { data: { n_kept: set.frames.length } });
          tel.dispose();
        }).catch(() => undefined);
      });
    }, 400);
    return () => clearTimeout(h);
  }, [set]);

  const handOver = (files: File[], captions: string[], labels?: ScanPhotoLabel[]) => {
    files.forEach((f, i) => rememberCaption(f, captions[i] || "", labels?.[i]));
    onCapturedRef.current(files);
    handedRef.current = files.length;
    setHanded(files.length);
    setOpen(false);
  };

  const finish = async (out: FlowResult): Promise<string | null> => {
    if (out.kind === "handoff") {
      handOver(out.frames.map((f) => f.file), out.frames.map((f) => f.caption));
      return null;
    }
    // THE TRIM, again: the server may have stopped taking extras since the scan was saved.
    const kept = withinExtras(out.set, extraMax);
    if (kept !== out.set) setSet(kept);
    const frames = orderedFrames(kept);
    handOver(framesToFiles(kept), frames.map((f) => captionFor(f, subject)), frames.map(labelOf));
    return null;
  };

  const startOver = () => {
    setSet(emptySet());
    handedRef.current = 0;
    setHanded(0);
    onCapturedRef.current([]);
    void clearSavedScan();
  };

  const cov = coverageOf(set);
  const started = set.frames.length > 0;

  // Scan first (owner, 2026-09-30): one big button. What the scan captures and
  // where it goes is on its first screen; the photo guidance lives only in the
  // no-camera photo checklist.
  return (
    <div data-testid="facelock-scan-card" data-state={handed ? "handed" : started ? "started" : "new"} className="grid gap-2">
      {handed ? (
        <p className="text-sm font-semibold text-primary" data-testid="facelock-scan-ready">{t("faceLock.scan.ready", { count: handed })}</p>
      ) : started ? (
        <p className="text-sm" data-testid="facelock-scan-saved">{t("faceLock.scan.resumeTitle", { count: cov.total, total: set.mode === "upload" ? cov.rows.length : planTotal(planOf(set)) })}</p>
      ) : null}
      <Button type="button" size="xl" disabled={disabled} onClick={openScan} data-testid="facelock-scan-start"
        variant={handed ? "outline" : "default"} className="w-full [&_svg]:size-6">
        <ScanFace aria-hidden /> {handed ? t("faceLock.scan.reviewAgain") : started ? t("faceLock.scan.resume") : t("faceLock.scan.start")}
      </Button>
      {started && (
        <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={startOver} data-testid="facelock-scan-start-over" className="justify-self-center">
          {t("faceLock.scan.startOver")}
        </Button>
      )}
      {open && (
        <React.Suspense fallback={<div className="fixed inset-0 z-[420] flex items-center justify-center bg-[#0b0c0e] text-[#f4f5f7]"><Loader2 className="h-10 w-10 motion-safe:animate-spin" aria-label={t("faceLock.scan.loading")} /></div>}>
          <ScanFlow
            set={set}
            onSet={update}
            onFinish={finish}
            onClose={() => setOpen(false)}
            finishLabel={(count) => t("faceLock.scan.review.use", { count })}
            telemetrySession={telemetrySession ?? undefined}
          />
        </React.Suspense>
      )}
    </div>
  );
}

/** The dialog's scanSlot: `<NewFaceLockDialog scanSlot={faceLockScanSlot} … />`. */
export const faceLockScanSlot = (props: FaceLockScanSlotProps) => <FaceLockScan {...props} />;
